import { useRef, useEffect, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { ProShield } from "@/components/Brand";
import { FOUNDING_TOTAL, FOUNDING_TAKEN, FOUNDING_LEFT } from "@/lib/brand";
import { Check, Lock, ArrowUpRight } from "lucide-react";

function CountUp({ to, duration = 1300 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let raf;
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / duration);
      setN(Math.round((1 - Math.pow(1 - p, 3)) * to));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration]);
  return <span ref={ref}>{n}</span>;
}

function FoundingCounter() {
  const pct = (FOUNDING_TAKEN / FOUNDING_TOTAL) * 100;
  return (
    <div data-testid="founding-counter" className="mt-8 rounded-2xl border border-[#F5A623]/30 bg-[#F5A623]/[0.06] p-5">
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-2 text-sm font-semibold text-white/80">
          <span className="relative flex w-2.5 h-2.5">
            <span className="absolute inline-flex w-full h-full rounded-full bg-[#F5A623] opacity-60 pulse-ring" />
            <span className="relative inline-flex w-2.5 h-2.5 rounded-full bg-[#F5A623]" />
          </span>
          Founding spots remaining
        </span>
        <span className="font-display font-extrabold text-2xl text-[#F5A623] leading-none">
          <CountUp to={FOUNDING_LEFT} /> <span className="text-white/40 text-lg">/ {FOUNDING_TOTAL}</span>
        </span>
      </div>
      <div className="mt-3 h-2 w-full rounded-full bg-white/10 overflow-hidden">
        <motion.div
          className="h-full rounded-full bg-[#F5A623]"
          initial={{ width: 0 }}
          whileInView={{ width: `${pct}%` }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
        />
      </div>
      <p className="mt-2.5 text-xs text-white/50">
        Only {FOUNDING_LEFT} left — once they're gone, founding pricing closes for good.
      </p>
    </div>
  );
}

const INCLUDES = [
  "Everything in DriveAssure Instructor",
  "Supervisor Tablet Mode",
  "Founding Member badge",
  "Direct line to the founder for feature requests",
];

export default function FounderOffer() {
  return (
    <section id="founder" data-testid="founder-section" className="bg-[#F7F7F7] py-20 lg:py-28">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <Reveal>
          {/* glowing gold border wrapper */}
          <div className="relative rounded-[2rem] p-[1.5px] overflow-hidden">
            <div
              className="absolute inset-[-100%]"
              style={{
                background:
                  "conic-gradient(from 0deg, transparent 0deg, #F5A623 60deg, transparent 160deg, transparent 360deg)",
                animation: "goldspin 8s linear infinite",
              }}
            />
            <div className="relative rounded-[calc(2rem-1.5px)] bg-[#2B2B2B] grain overflow-hidden">
              <div className="absolute inset-0 techgrid-dark opacity-50" />
              <div className="relative z-10 grid lg:grid-cols-12 gap-10 p-8 sm:p-12 lg:p-16 items-center">
                <div className="lg:col-span-7 text-white">
                  <span className="inline-flex items-center gap-2 rounded-full border border-[#F5A623]/40 bg-[#F5A623]/10 px-4 py-1.5 overline text-[#F5A623]">
                    <Lock size={13} /> Founder Program
                  </span>
                  <h2 className="mt-6 font-display font-extrabold tracking-tight text-4xl sm:text-5xl leading-[1.02]">
                    Lock A$30/month.
                    <span className="block text-[#F5A623]">For life.</span>
                  </h2>
                  <p className="mt-5 max-w-xl text-white/70 leading-relaxed">
                    We're opening DriveAssure to just 30 founding instructors — A$30/month, or A$300/year
                    with two months free. Join now and your price never changes, even as we add tools and
                    raise prices for everyone else.
                  </p>

                  <FoundingCounter />

                  <ul className="mt-8 grid sm:grid-cols-2 gap-3">
                    {INCLUDES.map((f) => (
                      <li key={f} className="flex items-start gap-2.5">
                        <span className="grid place-items-center w-5 h-5 mt-0.5 rounded-full bg-[#F5A623] text-[#2B2B2B] shrink-0">
                          <Check size={12} strokeWidth={3} />
                        </span>
                        <span className="text-sm text-white/85">{f}</span>
                      </li>
                    ))}
                  </ul>

                  <a
                    href="#download"
                    data-testid="founder-join-btn"
                    className="group mt-10 inline-flex items-center gap-2 rounded-full bg-[#F5A623] px-7 py-4 font-semibold text-[#2B2B2B] hover:bg-[#e0940f] transition-colors"
                  >
                    Join the Founder Program
                    <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </a>
                </div>

                {/* seal */}
                <div className="lg:col-span-5 flex justify-center">
                  <div className="relative">
                    <div className="absolute inset-0 -m-8 rounded-full bg-[#F5A623]/15 blur-2xl" />
                    <div className="relative rounded-3xl border border-white/10 bg-white/[0.04] px-10 py-12 text-center backdrop-blur">
                      <ProShield size={92} className="mx-auto" />
                      <p className="mt-6 font-display font-extrabold text-2xl text-white">Founding Member</p>
                      <p className="mt-1 text-sm text-white/50">#001 – #030</p>
                      <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#F5A623]/12 px-4 py-2 text-xs font-semibold text-[#F5A623]">
                        <Lock size={13} /> Price locked for life
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
